import { Store } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { QueryClient } from "react-query";
import { Persistor } from "redux-persist";

import {
  SESSION_INVALIDATED_BY_LOGOUT_CODE,
  SESSION_INVALIDATED_MESSAGE,
  isSessionInvalidation401,
} from "../constant/sessionConstants";
import { performStateCleanup } from "../services/standardizedLogout";
import { showSessionKickedOverlay } from "./sessionKickedOverlay";
import {
  claimSessionInvalidation,
  resetSessionInvalidation,
  isLoggingOut,
  setLoggingOut,
} from "./sessionState";

/**
 * Custom error thrown when a session is invalidated by a new login.
 * React Query global error handlers should filter this out to avoid
 * spurious error toasts during session cleanup.
 */
export class SessionInvalidatedError extends Error {
  constructor() {
    super("Session invalidated");
    this.name = "SessionInvalidatedError";
  }
}

interface SetupInterceptorParams {
  store: Store;
  resetActions?: Array<() => { type: string }>;
  persistor?: Persistor;
  queryClient?: QueryClient;
}

let isInterceptorSetup = false;
let interceptorId: number | null = null;

// Re-export setLoggingOut so existing callers (logout services) still work
export { setLoggingOut };

/**
 * Whether this interceptor is currently registered on the global axios
 * instance.
 *
 * The generic unauthorized interceptor consults this before SKIPPING a
 * session-invalidation 401: skipping is only correct if someone is actually
 * going to claim the error. This interceptor registers in
 * SessionInterceptorProvider's mount effect, while the generic one registers
 * at module scope in src/index.tsx — so during the window before first render
 * commit (and in any app that never mounts the provider) an unconditional
 * skip would leave a session-kill 401 handled by nobody at all, which is
 * worse than the plain redirect it replaced.
 */
export const isSessionInterceptorRegistered = (): boolean =>
  isInterceptorSetup && interceptorId !== null;

/**
 * Sets up an Axios response interceptor to handle session invalidation.
 * This should be called once when the app initializes.
 */
export const setupSessionInterceptor = ({
  store,
  resetActions,
  persistor,
  queryClient,
}: SetupInterceptorParams): void => {
  // Prevent duplicate interceptors
  if (isInterceptorSetup && interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        const data = error.response.data as { code?: string; message?: string };

        // Shared predicate (sessionConstants) — the same one the generic
        // unauthorized interceptor uses to SKIP these errors, so exactly one
        // of the two interceptors ever acts on a given 401.
        if (isSessionInvalidation401(error)) {
          // If user is intentionally logging out, suppress the notification and let logout handle cleanup
          if (isLoggingOut()) {
            setLoggingOut(false);
            return Promise.reject(error);
          }

          // Try to claim the invalidation lock — if someone else (WebSocket) already claimed it, just reject quietly
          if (!claimSessionInvalidation()) {
            return Promise.reject(new SessionInvalidatedError());
          }

          // Wipe local state clean immediately (so refresh won't restore the session)
          await performStateCleanup({ store, resetActions, persistor, queryClient });

          // For logout events, silently redirect — no overlay needed
          if (data?.code === SESSION_INVALIDATED_BY_LOGOUT_CODE) {
            resetSessionInvalidation();
            window.location.href = "/login";
            return Promise.reject(new SessionInvalidatedError());
          }

          // For new login from another device, show overlay
          showSessionKickedOverlay(SESSION_INVALIDATED_MESSAGE, () => {
            resetSessionInvalidation();
            window.location.href = "/login";
          });

          return Promise.reject(new SessionInvalidatedError());
        }
      }
      return Promise.reject(error);
    },
  );

  isInterceptorSetup = true;
};

/**
 * Removes the session interceptor. Call this on cleanup if needed.
 */
export const removeSessionInterceptor = (): void => {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
    isInterceptorSetup = false;
    resetSessionInvalidation();
  }
};

export default setupSessionInterceptor;
